import * as React from 'react';
import { usePaymentPlans } from './usePaymentPlans';
import { Drawer, IconButton, LabelButton, Toggle } from '@teamsnap/snap-ui';
import {
  getDepositDescription,
  getInstallmentsDescription,
} from '../installment-utils';
import { PaymentPlansForm } from './PaymentPlansForm';
import { LoadingType } from './types';
import './PaymentPlans.scss'

interface Props {
  scopeId: number;
  scopeType: string;
  orgId: number;
  currencySymbol?: string;
  currencyCode?: string;
}

const PaymentPlans: React.FC<Props> = ({
  scopeId,
  scopeType,
  currencySymbol = '$',
  currencyCode = 'USD',
}) => {
  const {
    loading,
    plans,
    isOpen,
    formValues,
    errors,
    addPlan,
    editPlan,
    updateField,
    closeDrawer,
    togglePlan,
    savePlan,
    isSaveDisabled,
  } = usePaymentPlans({ scopeId, scopeType });

  const renderPlans = React.useCallback(
    () =>
      plans.map((plan) => (
        <div
          className="sui-border sui-border-solid sui-border-neutral-border sui-rounded sui-p-2 sui-mb-1"
          key={plan.id}
          data-testid={`PaymentPlans--plan-${plan.id}`}
        >
          <div className="sui-flex sui-w-full sui-items-center sui-mb-2 sui-gap-2">
            <Toggle
              name={plan.id.toString()}
              on={plan.enabled}
              onClick={() => {
                togglePlan(plan.id, !plan.enabled);
              }}
              disabled={loading[LoadingType.CreateOrUpdatePlan] && loading.id === plan.id}
              data-testid={`PaymentPlans--toggle-plan-${plan.id}`}
            />
            <span className="sui-flex-grow sui-heading-sm" title={plan.name}>
              {plan.name}
            </span>
            <IconButton
              icon="edit"
              sentiment="neutral"
              onClick={() => {
                editPlan(plan.id);
              }}
              data-testid={`PaymentPlans--edit-plan-${plan.id}`}
              disabled={loading[LoadingType.CreateOrUpdatePlan] && loading.id === plan.id}
            />
          </div>
          <div className="sui-pb-1">
            <div className="sui-mb-1" data-testid={'PaymentPlans--plan-summary-deposit'}>
              Deposit: {getDepositDescription(plan, currencySymbol)}
            </div>
            <div data-testid={'PaymentPlans--plan-summary-schedule'}>{getInstallmentsDescription(plan)}</div>
          </div>
        </div>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plans, currencySymbol, editPlan]
  );

  return (
    <>
      <div data-testid="PaymentPlans" className="sui-flex sui-flex-col">
        <div className="sui-flex sui-justify-end sui-mb-2">
          <LabelButton
            onClick={() => addPlan()}
            icon="add"
            iconPosition="left"
            labelText="Add payment plan"
            data-testid="PaymentPlans--add-plan"
          />
        </div>

        {plans.length > 0 ? (
          <div data-testid="PaymentPlans-list">{renderPlans()}</div>
        ) : (
          <p data-testid="PaymentPlans-empty">No payment plans created.</p>
        )}
      </div>
      <Drawer
        title=""
        open={isOpen}
        size="large"
        testId="PaymentPlansDrawer"
        onCloseClick={closeDrawer}
        onClickOutside={closeDrawer}
        className='foo'
      >
        <Drawer.Header>
          <span
            className="sui-heading-md"
            data-testid="PaymentPlansDrawer--header"
          >
            {formValues?.id ? 'Edit payment plan' : 'Payment plan'}
          </span>
          <IconButton
            data-testid="PaymentPlansDrawer--close"
            icon="close"
            onClick={closeDrawer}
            className="sui-ml-auto sui-text-gray-7"
          />
        </Drawer.Header>
        <Drawer.Content
          className="sui-body"
          data-testid="PaymentPlansDrawer--content"
        >
          {formValues && isOpen && (
            <PaymentPlansForm {...{ formValues, errors, updateField, currencyCode }} />
          )}
        </Drawer.Content>
        <Drawer.Footer
          className="sui-flex"
          data-testid="PaymentPlansDrawer--footer"
        >
          <LabelButton
            onClick={closeDrawer}
            labelText="Cancel"
            variantType="secondary"
            data-testid="PaymentPlansDrawer--cancel"
          />
          <LabelButton
            onClick={savePlan}
            disabled={isSaveDisabled}
            loading={loading[LoadingType.CreateOrUpdatePlan]}
            labelText="Save"
            data-testid="PaymentPlansDrawer--save"
          />
        </Drawer.Footer>
      </Drawer>
    </>
  );
};

export default PaymentPlans;
